
import { useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { toast } from "sonner";

export const useReferralTrackingEnhanced = () => {
  // Track referral clicks with better error handling
  const trackClick = useMutation({
    mutationFn: async (referralCode: string) => {
      console.log('Tracking referral click for code:', referralCode);
      
      const { data: codeData, error: codeError } = await supabase
        .from('referral_codes')
        .select('id, user_id')
        .eq('code', referralCode)
        .eq('is_active', true)
        .single();

      if (codeError || !codeData) {
        console.error('Referral code not found:', codeError);
        throw new Error('Invalid referral code');
      }

      // Track the click
      const { error: clickError } = await supabase
        .from('referral_clicks')
        .insert({
          referral_code_id: codeData.id,
          user_agent: navigator.userAgent,
          utm_source: new URLSearchParams(window.location.search).get('utm_source'),
          utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
          utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign'),
        });

      if (clickError) {
        console.error('Error tracking click:', clickError);
        throw clickError;
      }

      return codeData;
    },
    onSuccess: (data) => {
      console.log('Successfully tracked referral click for user:', data.user_id);
    },
    onError: (error) => {
      console.error('Failed to track referral click:', error);
    }
  });

  // Get current stored referral code
  const getReferralCode = () => {
    return localStorage.getItem('referral_code');
  };

  // Clear referral code (used after conversion)
  const clearReferralCode = () => {
    localStorage.removeItem('referral_code');
    console.log('Cleared referral code from localStorage');
  };

  // Set referral code with expiration
  const setReferralCode = (code: string) => {
    const expiration = Date.now() + (7 * 24 * 60 * 60 * 1000); // 7 days
    const referralData = {
      code,
      expires: expiration
    };
    localStorage.setItem('referral_code', JSON.stringify(referralData));
    console.log('Stored referral code with expiration:', referralData);
  };

  // Get referral code with expiration check
  const getValidReferralCode = () => {
    try {
      const stored = localStorage.getItem('referral_code');
      if (!stored) return null;

      // Handle legacy string format
      if (typeof stored === 'string' && !stored.startsWith('{')) {
        return stored;
      }

      const referralData = JSON.parse(stored);
      if (referralData.expires && Date.now() > referralData.expires) {
        console.log('Referral code expired, clearing');
        clearReferralCode();
        return null;
      }

      return referralData.code || referralData;
    } catch (error) {
      console.error('Error parsing referral code:', error);
      clearReferralCode();
      return null;
    }
  };

  // Auto-track referral clicks on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    
    if (refCode) {
      console.log('Detected referral code in URL:', refCode);
      
      // Store referral code for later conversion tracking
      setReferralCode(refCode);
      
      // Track the click
      trackClick.mutate(refCode);
      
      // Clean up URL without causing a reload
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      
      // Show success message
      toast.success('Referral link tracked! Sign up to help your referrer earn a commission.');
    }
  }, []);

  return { 
    trackClick, 
    getReferralCode: getValidReferralCode,
    clearReferralCode,
    setReferralCode,
    isTracking: trackClick.isPending
  };
};
